import React from 'react';
import { useInView } from 'react-intersection-observer';

// --- Data updated to reflect design materials and concepts ---
const materialsTop = [
  { name: 'Natural Stone', icon: 'fas fa-gem' },
  { name: 'Walnut Wood', icon: 'fas fa-tree' },
  { name: 'Velvet Fabric', icon: 'fas fa-tshirt' },
  { name: 'Concrete Finish', icon: 'fas fa-building' },
  { name: 'Color Palette', icon: 'fas fa-fill-drip' },
  { name: 'Metal Accents', icon: 'fas fa-wrench' },
];

const materialsBottom = [
  { name: 'Sleek Leather', icon: 'fas fa-couch' },
  { name: 'Stainless Steel', icon: 'fas fa-cog' },
  { name: 'Minimalist Glass', icon: 'fas fa-shapes' },
  { name: '3D Visualization', icon: 'fas fa-cube' },
  { name: 'Lighting Plans', icon: 'fas fa-lightbulb' },
  { name: 'High-Gloss Paint', icon: 'fas fa-palette' },
];


function MaterialsShowcase() {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });

  const Marquee = ({ technologies, direction = 'left' }) => (
    <div className="marquee-content flex w-max">
      {/* Render the list twice for a seamless loop */}
      {[...technologies, ...technologies].map((tech, index) => (
        <div
          key={`${tech.name}-${index}`}
          className={`
            subtle-gradient-border relative group flex flex-col items-center justify-center space-y-3 p-6 mx-4
            w-32 h-32
            bg-white/60 backdrop-blur-sm rounded-2xl shadow-lg
            transition-all duration-300 ease-in-out hover:shadow-red-500/50 hover:shadow-2xl hover:-translate-y-2
          `}
        >
          {/* Inner hover shine - Gradient changed to Red and Black */}
          <div className="absolute -inset-px bg-gradient-to-r from-red-600 to-black rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" aria-hidden="true"></div>
          
          <div className="relative flex flex-col items-center justify-center">
            {/* Icon color changed to red on hover */}
            <i className={`${tech.icon} text-5xl text-gray-500 group-hover:text-red-600 transition-colors duration-300`}></i>
            <span className="mt-4 text-sm font-semibold text-gray-700 group-hover:text-gray-900 transition-colors duration-300">{tech.name}</span>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <section ref={ref} className="bg-white py-20 relative overflow-hidden">
        {/* Background Grid Pattern */}
        <div className="absolute inset-0 z-0 opacity-50">
            <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:1.5rem_1.5rem]"></div>
        </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          {/* Updated Headline for Materials Library */}
          <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900">Our Library of Signature Materials</h2>
          <p className="mt-4 max-w-2xl mx-auto text-xl text-gray-600">
            Explore the core elements from high contrast finishes to essential textures that define our bold design style.
          </p>
        </div>
        
        {/* --- Infinite Scrolling Marquee Container --- */}
        <div className={`space-y-8 transition-opacity duration-1000 ease-out ${inView ? 'opacity-100' : 'opacity-0'}`}>
            {/* Top row, scrolls left */}
            <div className="w-full overflow-hidden">
                <div className="marquee-left">
                    <Marquee technologies={materialsTop} />
                </div>
            </div>
            {/* Bottom row, scrolls right */}
            <div className="w-full overflow-hidden">
                <div className="marquee-right">
                    <Marquee technologies={materialsBottom} />
                </div>
            </div>
        </div>
      </div>
      <style jsx>{`
        @keyframes marquee-left {
            from { transform: translateX(0); }
            to { transform: translateX(-50%); }
        }
        .marquee-left {
            animation: marquee-left 7s linear infinite; /* FASTER scroll (7s) for desktop/tablet */
        }

        @keyframes marquee-right {
            from { transform: translateX(-50%); }
            to { transform: translateX(0); }
        }
        .marquee-right {
            animation: marquee-right 7s linear infinite; /* FASTER scroll (7s) for desktop/tablet */
        }

        /* Speed Adjustment for Mobile Screens (768px and smaller) */
        @media (max-width: 768px) {
            .marquee-left {
                animation-duration: 4s; /* EXTREME SPEED for mobile */
            }
            .marquee-right {
                animation-duration: 4s; /* EXTREME SPEED for mobile */
            }
        }

        /* --- Updated Subtle Gradient Border Style (Red/Black/White) --- */
        .subtle-gradient-border::before {
            content: "";
            position: absolute;
            inset: 0;
            border-radius: 1rem; /* Tailwind's rounded-2xl */
            padding: 1px; /* border thickness */
            /* Gradient: subtle dark gray to red accent */
            background: linear-gradient(135deg, rgba(51, 51, 51, 0.7), rgba(255, 0, 0, 0.7)); 
            -webkit-mask:
                linear-gradient(#fff 0 0) content-box,
                linear-gradient(#fff 0 0);
            -webkit-mask-composite: xor;
            mask-composite: exclude;
            pointer-events: none;
        }
      `}</style>
    </section>
  );
}

export default MaterialsShowcase;
